import { adminAxios } from "./axiosClient";

export interface AdminUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
  is_superuser: boolean;
}

export interface DashboardStats {
  categories: number;
  subcategories: number;
  products: number;
  blogs: number;
  clients: number;
  projects: number;
  images: number;
  featured_products: number;
}

export const adminLogin = async (username: string, password: string) => {
  const { data } = await adminAxios.post("/auth/login/", { username, password });
  return data as {
    access: string;
    refresh: string;
    user: AdminUser;
  };
};

export const adminMe = async () => {
  const { data } = await adminAxios.get("/auth/me/");
  return data as AdminUser;
};

export const getDashboardStats = async () => {
  const { data } = await adminAxios.get("/dashboard/");
  return data as DashboardStats;
};

export const listResource = async <T = any>(
  path: string,
  params?: Record<string, any>
) => {
  const { data } = await adminAxios.get(path, { params });
  // DRF may return array or paginated {results}
  if (Array.isArray(data)) return data as T[];
  if (data?.results) return data.results as T[];
  return data as T[];
};

export const getResource = async <T = any>(path: string) => {
  const { data } = await adminAxios.get(path);
  return data as T;
};

export const createResource = async <T = any>(path: string, body: any) => {
  const { data } = await adminAxios.post(path, body);
  return data as T;
};

export const updateResource = async <T = any>(path: string, body: any) => {
  const { data } = await adminAxios.put(path, body);
  return data as T;
};

export const patchResource = async <T = any>(path: string, body: any) => {
  const { data } = await adminAxios.patch(path, body);
  return data as T;
};

export const deleteResource = async (path: string) => {
  await adminAxios.delete(path);
};

export const uploadFile = async (
  file: File,
  folder:
    | "product_images"
    | "product_pdfs"
    | "clients"
    | "projects"
    | "categories" = "product_images"
) => {
  const form = new FormData();
  form.append("file", file);
  form.append("folder", folder);
  const { data } = await adminAxios.post("/upload/", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data as { url: string; path: string; name: string };
};

export const uploadProductPdf = async (
  sku: string,
  file: File,
  slot?: number
) => {
  const form = new FormData();
  form.append("file", file);
  if (slot) form.append("slot", String(slot));
  const { data } = await adminAxios.post(
    `/products/${encodeURIComponent(sku)}/upload-pdf/`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
};

export const deleteProductPdf = async (sku: string, slot: number) => {
  const { data } = await adminAxios.delete(
    `/products/${encodeURIComponent(sku)}/pdfs/${slot}/`
  );
  return data;
};

export const getDownloadsLibrary = async () => {
  const { data } = await adminAxios.get("/downloads/");
  return data as {
    count: number;
    results: Array<{
      product_id: number;
      product_sku: string;
      product_name: string;
      slot: number;
      name: string;
      url: string;
    }>;
  };
};
